import React, { FC, memo, useCallback } from 'react';
import styled from '@emotion/styled';
import { useI18n } from '~Hooks';
import useIsLandscape from '../../Hooks/useIsLandscape';
import GlassPill from '../../Components/GlassPill';
import GlassTag from '../../Components/GlassPill/GlassTag';
import { SERVICE_TYPE } from '../../../../../__queries__/globalTypes';
import { useEposTheme } from '../../Providers/ThemeProvider';
import { useExpressCart } from '../../Providers/ExpressCartProvider';
import eatHereIcon from '../../../../Assets/icons/EPOS/eat_here.svg';
import takeAwayIcon from '../../../../Assets/icons/EPOS/take_away.svg';
import arrowBackIcon from '../../../../Assets/icons/arrow_back.svg';

/**
 * Sits in the free strip above the menu sheet, over the photo background:
 * "start over" on the left, the venue icon in the middle, and the chosen
 * service type as a tag on the right. Three equal columns keep the icon on the
 * true center line no matter how wide the pill and the tag are.
 */
const Bar = styled.header`
  flex: 0 0 auto;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 16px;
  padding: 24px 16px 0;
  box-sizing: border-box;
`;

const Left = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const Right = styled.div`
  display: flex;
  justify-content: flex-end;
`;

/** Kept as tall as the pills beside it so it never grows down under the menu sheet. */
const VenueIcon = styled.img`
  height: 72px;
  max-width: 240px;
  object-fit: contain;

  @media (orientation: landscape) {
    height: 56px;
  }
`;

interface Props {
  onStartOver: () => void;
}

const Header: FC<Props> = ({ onStartOver }) => {
  const { i18n } = useI18n();
  const eposTheme = useEposTheme();
  const expressCart = useExpressCart();

  const handleStartOver = useCallback(() => onStartOver(), [onStartOver]);
  /** The strip above the sheet is shorter in landscape, so the pills step down a size. */
  const isLandscape = useIsLandscape();
  const pillSize = isLandscape ? 'medium' : 'large';

  const isEatHere = expressCart.serviceType === SERVICE_TYPE.EAT_HERE;

  return (
    <Bar>
      <Left>
        <GlassPill
          size={pillSize}
          icon={arrowBackIcon}
          onClick={handleStartOver}
          testId="epos-start-over">
          {i18n('Express.StartOver')}
        </GlassPill>
      </Left>

      {eposTheme.iconImageSrcUrl ? (
        <VenueIcon src={eposTheme.iconImageSrcUrl} alt="" draggable={false} />
      ) : (
        <span />
      )}

      <Right>
        <GlassTag size={pillSize} icon={isEatHere ? eatHereIcon : takeAwayIcon}>
          {isEatHere ? i18n('ServiceType.EatHere') : i18n('ServiceType.TakeAway')}
        </GlassTag>
      </Right>
    </Bar>
  );
};

export default memo(Header);
